/**
 * Il campo allegato (punto 13).
 *
 * Le regole sono **quelle di `TaskDetailDialog`**, non una versione somigliante:
 * il tetto degli allegati (`MAX_ATTACHMENTS`), lo spazio della subscription, e
 * la deduplica per nome. Chi carica un file dal modello e chi lo carica dal task
 * sta consumando la stessa risorsa, quindi le due porte devono dire di no alle
 * stesse condizioni.
 *
 * Quelle che stanno qui sono le regole che si possono verificare **senza un
 * browser**: quante ne passano, quali si saltano, e cosa succede al valore. Il
 * controllo che disegna il campo, e la chiamata di caricamento, stanno fuori —
 * e non c'è niente da decidere lì che non sia deciso qui.
 */

import { MAX_ATTACHMENTS } from "common/attachmentLimits";
import type { Attachment } from "components/AttachmentList";
import type { UserData } from "hooks/useUserDataKeys";

/** Un file scelto dall'utente, ridotto a ciò che serve per decidere. */
export interface PickedFile {
  name: string;
  size: number;
}

export interface AttachmentBudget {
  /** Quanti allegati ha già il processo. */
  existing: number;
  /** Spazio già occupato dalla subscription, in byte. */
  using: number;
  /** Spazio concesso dalla subscription, in byte. */
  permit: number;
  /** La subscription non si applica (amministratore, o ambiente di prova). */
  bypass: boolean;
}

/**
 * La chiave del problema, o `null` se si può caricare.
 *
 * Generica sui file: il verdetto decide su nome e dimensione, ma restituisce
 * **gli stessi oggetti** che ha ricevuto — chi chiama deve poterli caricare, e
 * un `File` non si ricostruisce da un `PickedFile`.
 */
export interface AttachmentVerdict<T extends PickedFile> {
  /** I file che verrebbero davvero caricati. */
  accepted: T[];
  /** I file saltati perché un allegato con quel nome c'è già. */
  duplicated: T[];
  /** La chiave da mostrare, o `null`. */
  issueKey: string | null;
}

/**
 * Cosa succede se si caricano questi file.
 *
 * Le due condizioni sono **le stesse di `TaskDetailDialog`**, e nell'ordine in
 * cui le controlla lui: prima lo spazio, poi il tetto. L'ordine conta perché
 * quando falliscono entrambe si mostra un avviso solo, e deve essere quello
 * giusto: "hai finito lo spazio" è una cosa che l'utente può risolvere, "hai
 * dieci allegati" no.
 *
 * La deduplica **non è un errore**: il file che c'è già si salta, e gli altri
 * passano. Oggi nel dialog è una snackbar informativa, non un rifiuto.
 */
export function attachmentVerdict<T extends PickedFile>(
  files: readonly T[],
  existingNames: readonly string[],
  budget: AttachmentBudget
): AttachmentVerdict<T> {
  const already = new Set(existingNames);
  const accepted: T[] = [];
  const duplicated: T[] = [];

  for (const file of files) {
    if (already.has(file.name)) {
      duplicated.push(file);
      continue;
    }
    // Anche fra i file appena scelti: due file con lo stesso nome nella stessa
    // scelta sono lo stesso allegato due volte.
    already.add(file.name);
    accepted.push(file);
  }

  const size = accepted.reduce((total, file) => total + file.size, 0);
  if (!budget.bypass && budget.using + size > budget.permit) {
    return { accepted, duplicated, issueKey: "storage.limit.reached" };
  }

  if (budget.existing + accepted.length > MAX_ATTACHMENTS) {
    return { accepted, duplicated, issueKey: "attachment.limit.reached" };
  }

  return { accepted, duplicated, issueKey: null };
}

/**
 * Aggiunge gli id degli allegati a un valore, senza ripeterli.
 *
 * Lo stesso allegato due volte nello stesso campo non è un'informazione: è lo
 * stesso file, e mostrarlo due volte farebbe credere a due documenti. L'ordine
 * è quello in cui sono stati caricati, che è l'unico ordine che l'utente
 * riconosce.
 */
export function withAttachmentIds(
  value: readonly string[],
  ids: readonly (string | number)[]
): string[] {
  const next = [...value];
  for (const id of ids) {
    const key = String(id);
    if (!next.includes(key)) next.push(key);
  }
  return next;
}

/** Toglie un id dal valore, lasciando gli altri nell'ordine in cui sono. */
export const withoutAttachmentId = (
  value: readonly string[],
  id: string | number
): string[] => value.filter((current) => current !== String(id));

/**
 * Ciò che serve al campo per caricare.
 *
 * Lo fornisce la superficie che ha il task — cioè il dialog del task — e non il
 * renderer dei campi: il caricamento passa da
 * `flownodes/{idprocesstoken}/attachments`, e `idprocesstoken` è l'istanza del
 * flownodo nel modello dell'app, che il contesto del contratto non porta. Dove
 * questo oggetto non c'è, il campo si mostra e non si compila.
 */
export interface AttachmentHost {
  onFieldAttachmentIdsChange?: (ids: readonly string[]) => void;
  /**
   * Gli allegati del processo, interi.
   *
   * L'id serve al valore, il nome alla deduplica, ma la riga del campo li mostra
   * **con gli stessi comandi dell'elenco allegati** (anteprima, rinomina,
   * elimina, scarica): per quello servono i record completi, non due colonne.
   */
  available: readonly Attachment[];
  /**
   * La lista degli allegati si sta ricaricando.
   *
   * Un id appena caricato e' gia' nel valore ma non ancora in `available`:
   * finche' la lista non arriva non e' «un allegato non piu' presente», e non
   * va mostrato come tale.
   */
  loading?: boolean;
  budget: AttachmentBudget;
  /**
   * Carica i file già accettati e restituisce i loro id.
   *
   * Riceve `File` e non `PickedFile`: il nome e la dimensione bastano a
   * decidere, ma a caricare serve il contenuto.
   */
  upload: (files: readonly File[]) => Promise<readonly (string | number)[]>;
  /**
   * Ricarica l'elenco allegati del processo dopo una modifica al file
   * (rinomina o elimina dalla riga del campo): senza, la riga mostrerebbe il
   * nome vecchio fino alla riapertura.
   */
  refresh?: () => Promise<void> | void;
  /**
   * La storia di ciascun allegato, per id: le versioni precedenti di quel file.
   *
   * La riga del campo le mostra **come l'elenco del task** — stesso comando,
   * stesse voci — perché è la stessa riga. La mappa la porta la superficie che
   * ha il task: è quella che `useAttachments` carica per l'elenco "di contorno",
   * e il campo non ha una rotta propria da cui chiederla.
   */
  histories?: { [key: string]: any };
}

/**
 * Il nome di ciascun allegato del valore, `null` se non c'è più.
 *
 * Un id senza allegato non si nasconde: quel campo mostrerebbe un documento che
 * non esiste, e nessuno saprebbe che è stato cancellato. È lo stesso istinto di
 * `missingReferences` nella visibilità.
 */
export function attachmentNames(
  value: readonly string[],
  available: readonly { id: number; filename: string }[]
): { id: string; filename: string | null }[] {
  const byId = new Map(available.map((item) => [String(item.id), item.filename]));
  return value.map((id) => ({ id, filename: byId.get(id) ?? null }));
}

/**
 * Gli id degli allegati a cui puntano i campi allegato di uno userdata.
 *
 * L'elenco "di contorno" del task li nasconde: quei file si gestiscono dalla
 * riga del campo che li riferisce, e mostrarli in due posti farebbe sembrare
 * l'elenco il posto dove si cancellano — dove sparirebbero da sotto il campo
 * che li nomina. Il valore di un campo allegato è un array di id, o una
 * stringa sola (la scelta "uno o molti" è reversibile).
 */
export function referencedAttachmentIds(userdata: {
  [key: string]: UserData;
}): Set<string> {
  const ids = new Set<string>();
  for (const entry of Object.values(userdata)) {
    if (entry?.type !== "attachment") continue;
    if (Array.isArray(entry.value)) {
      for (const id of entry.value) ids.add(String(id));
    } else if (typeof entry.value === "string" && entry.value !== "") {
      ids.add(entry.value);
    }
  }
  return ids;
}
